/**
 * Wo der Dateityp eines Feldes wohnt — beim Lesen und beim Speichern.
 *
 * Historisch gibt es nur **einen** Platz, und der ist zweckentfremdet: Bei einem einzelnen
 * Datei-Feld steht der Dateityp in `arrayType` (`type: 'file', arrayType: 'pdf'`), weil dort bei
 * einer Nicht-Liste nichts anderes stand. Eine **Liste von Dateien** besetzt diesen Platz aber
 * selbst (`type: 'array', arrayType: 'file'`) — sie hatte damit gar keinen Dateityp, und der
 * Editor zeigte für sie folgerichtig keine Auswahl an. Gemessen am 17.09.2026 an einem gespeicherten
 * Werkzeug:
 *
 *   {"name":"Eingabedateien","type":"array","arrayType":"file"}   ← kein Platz für „pdf"
 *   {"name":"Rollen","type":"file","arrayType":"any"}             ← Dateityp in `arrayType`
 *
 * Deshalb ein eigenes Feld `fileType` beim Speichern, und beim Lesen beides: Der alte Platz bleibt
 * gültig, damit vorhandene Werkzeuge unverändert weiterlaufen. Rein — keine Oberfläche, kein React.
 */

/** Die Dateitypen, die die Auswahl kennt. `any` heißt „gleich welcher". */
export const DATEITYPEN = [
  'markdown',
  'text',
  'image',
  'pdf',
  'excel',
  'word',
  'powerpoint',
  'any',
] as const;

export type Dateityp = (typeof DATEITYPEN)[number];

/** So viel braucht die Abbildung von einem Feld. */
export interface FeldMitDateityp {
  type?: string;
  arrayType?: string;
  fileType?: string;
}

const istDateityp = (wert: unknown): wert is Dateityp =>
  typeof wert === 'string' && (DATEITYPEN as readonly string[]).includes(wert);

/**
 * Der Dateityp eines gespeicherten Feldes.
 *
 * Der alte Platz gewinnt, wo er belegt ist: Ein Feld, das seit Monaten `arrayType: 'pdf'` trägt,
 * meint genau das — auch wenn daneben irgendwann ein `fileType` gelandet ist.
 */
export const dateitypLesen = (feld: FeldMitDateityp): Dateityp => {
  if (feld?.type === 'file' && istDateityp(feld.arrayType) && feld.arrayType !== 'any') {
    return feld.arrayType;
  }
  return istDateityp(feld?.fileType) ? feld.fileType : 'any';
};

/**
 * Was beim Speichern in `arrayType` und `fileType` gehört.
 *
 * Bei einem einzelnen Datei-Feld wandert der Typ weiterhin nach `arrayType` — alles, was heute
 * liest, sucht ihn dort. Zusätzlich steht er in `fileType`, und das ist für eine Liste von Dateien
 * der einzige Platz. `any` wird nicht geschrieben: Es ist die Voreinstellung und stünde sonst in
 * jedem Feld.
 */
export const dateitypSpeichern = (
  feld: FeldMitDateityp
): { arrayType?: string; fileType?: Dateityp } => {
  const typ = istDateityp(feld?.fileType) ? feld.fileType : undefined;
  const arrayType = feld?.type === 'file' && typ ? typ : feld?.arrayType;
  return { arrayType, fileType: typ && typ !== 'any' ? typ : undefined };
};
